// types/enums.ts
// Essential enums for the ecosystem stability and UI components

// ============================================================================
// TASK ENUMS
// ============================================================================

export enum TaskStatus {
  CREATED = 'created',
  ON_HOLD = 'on-hold',
  IN_PROGRESS = 'in-progress',
  FINISHING = 'finishing',
  DONE = 'done',
  COLLECTED = 'collected',
  FAILED = 'failed',
  NONE = 'none',
}

/** Mission Tree & hierarchies */
export enum TaskType {
  MISSION_GROUP = 'mission-group',   // Folder/container for missions tree tasks
  MISSION = 'mission',
  MILESTONE = 'milestone',
  GOAL = 'goal',
  ASSIGNMENT = 'assignment',
  RECURRENT_GROUP = 'recurrent-group',      // Folder/container for recurrent tree tasks
  RECURRENT_TEMPLATE = 'recurrent-template',   // Sets frequency pattern for instances
  RECURRENT_INSTANCE = 'recurrent-instance',    // Spawned with due date from templates or Individual creation
  AUTOMATION = 'automation'            // Automation tasks (e.g., monthly close) for User and Agents
}

export enum RecurrentFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum TaskPriority {
  NOT_NOW = 'not-now',
  SLOW = 'slow',
  NORMAL = 'normal',
  IMPORTANT = 'important',
  URGENT = 'urgent',
}

// ============================================================================
// ITEM ENUMS
// ============================================================================

export enum ItemStatus {
  IN_PROGRESS = 'in-progress',
  CREATED = 'created',
  FOR_SALE = 'for-sale',
  SOLD = 'sold',
  TO_ORDER = 'to-order',
  TO_DO = 'to-do',
  GIFTED = 'gifted',
  RESERVED = 'reserved',
  CONSIGNMENT = 'consignment',
  OBSOLETE = 'obsolete',
  DAMAGED = 'damaged',
  IDLE = 'idle',
  LEGACY = 'legacy',
}

// All Items - Products, Bundles, Materials and Equipment
export enum ItemType {
  // MODEL_ITEM - Individual products that can be separated by model, subtype, site and collection
  DIGITAL = 'digital',
  ARTWORK = 'artwork',
  PRINT = 'print',
  STICKER = 'sticker',
  MERCH = 'merch',
  CRAFT = 'craft',
  // BUNDLE_ITEM - Business Logic Items that are Packs of the same Type of Item.
  BUNDLE = 'bundle',            // Pack of Stickers or Prints
  // RESOURCE_ITEM - Materials and Equipment for production
  MATERIAL = 'material',
  EQUIPMENT = 'equipment',
}

// ============================================================================
// SITE ENUMS
// ============================================================================

export enum SiteStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

// ============================================================================
// FINANCIAL & PAYMENT ENUMS
// ============================================================================

export enum PaymentMethod {
  FIAT_USD = 'fiat-usd',
  FIAT_CRC = 'fiat-crc',
  BTC = 'btc',
  CARD = 'card',
  SINPE = 'sinpe',
  PAYPAL = 'paypal',
  WIRE_TRANSFER = 'wire-transfer',
  GIFT = 'gift',
  EXCHANGE = 'exchange',
  OTHER = 'other'
}

export enum Currency {
  USD = 'USD',
  CRC = 'CRC',
  BTC = 'BTC',
  JUNGLE_COINS = 'J$'
}

// ============================================================================
// ENTITY ENUMS
// ============================================================================

export enum EntityType {
  ACCOUNT = 'account',
  LINK = 'link',
  TASK = 'task',
  ITEM = 'item',
  SALE = 'sale',
  FINANCIAL = 'financial',
  CHARACTER = 'character',
  SITE = 'site',
  PLAYER = 'player',
  SESSION = 'session',
  SETTLEMENT = 'settlement',
  BUSINESS = 'business',
  CONTRACT = 'contract'
}

export enum LogEventType {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DONE = 'DONE',
  COLLECTED = 'COLLECTED',
  CANCELLED = 'CANCELLED',
  CHARGED = 'CHARGED',
  PENDING = 'PENDING',
  SOLD = 'SOLD',
  MOVED = 'MOVED',
  GIFTED = 'GIFTED',
  DAMAGED = 'DAMAGED',
  LEVEL_UP = 'LEVEL_UP',
  POINTS_CHANGED = 'POINTS_CHANGED',
  WIN_POINTS = 'WIN_POINTS',
  LOST_POINTS = 'LOST_POINTS',
  ROLE_CHANGED = 'ROLE_CHANGED',
  OWNS_ITEM = 'OWNS_ITEM',
  REQUESTED_TASK = 'REQUESTED_TASK',
  PURCHASED = 'PURCHASED',
  TRANSACTED = 'TRANSACTED',
  BUSINESS_LINKED = 'BUSINESS_LINKED',
  EMAIL_VERIFIED = 'EMAIL_VERIFIED',
  PASSWORD_RESET = 'PASSWORD_RESET',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  ACTIVATED = 'ACTIVATED',
  DEACTIVATED = 'DEACTIVATED',
  UNCOMPLETED = 'UNCOMPLETED',
  ON_HOLD = 'ON_HOLD',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHING = 'FINISHING',
  FAILED = 'FAILED',
  BULK_IMPORT = 'BULK_IMPORT',
  BULK_EXPORT = 'BULK_EXPORT',
}

// ============================================================================
// IAM ENUMS
// ============================================================================

export enum CharacterRole {
  FOUNDER = 'founder',
  TEAM = 'team',
  AGENT = 'agent',
  CUSTOMER = 'customer',
  BENEFICIARY = 'beneficiary',
  PLAYER = 'player',
  APPRENTICE = 'apprentice',
  FAMILY = 'family',
  TOKENHOLDER = 'token-holder',
  PARTNER = 'partner',
  ADMIN = 'admin',
  DESIGNER = 'designer',
  MAKER = 'maker',
  SELLER = 'seller',
  RESEARCHER = 'researcher',
  DEV = 'dev',
  LIBRARIAN = 'librarian',
  COLLABORATOR = 'collaborator',
  STUDENT = 'student',
  ARTIST = 'artist',
  ARCHITECT = 'architect',
  ORCHESTRATOR = 'orchestrator',
  TEACHER = 'teacher',
  CYPHER = 'cypher',
  GAMER = 'gamer',
  COMMUNICATOR = 'communicator',
  PROMOTER = 'promoter',
  STRATEGIST = 'strategist',
  ANALYST = 'analyst',
  ENTREPRENEUR = 'entrepreneur',
  INNOVATOR = 'innovator',
  GURU = 'guru',
  PM = 'pm',
  MARKETER = 'marketer'
}

/** Character role types for UI organization and permissions */
export const CHARACTER_ROLE_TYPES = {
  REGULAR: [
    CharacterRole.ADMIN,
    CharacterRole.RESEARCHER,
    CharacterRole.DEV,
    CharacterRole.DESIGNER,
    CharacterRole.MAKER,
    CharacterRole.SELLER,
    CharacterRole.LIBRARIAN,
    CharacterRole.STUDENT,
    CharacterRole.ARTIST,
    CharacterRole.ARCHITECT,
    CharacterRole.ORCHESTRATOR,
    CharacterRole.TEACHER,
    CharacterRole.CYPHER,
    CharacterRole.GAMER,
    CharacterRole.COMMUNICATOR,
    CharacterRole.PROMOTER,
    CharacterRole.STRATEGIST,
    CharacterRole.ANALYST,
    CharacterRole.ENTREPRENEUR,
    CharacterRole.INNOVATOR,
    CharacterRole.GURU,
    CharacterRole.PM,
    CharacterRole.MARKETER
  ],
  SPECIAL: [
    CharacterRole.FOUNDER,
    CharacterRole.PLAYER,
    CharacterRole.APPRENTICE,
    CharacterRole.TEAM,
    CharacterRole.FAMILY,
    CharacterRole.TOKENHOLDER,
    CharacterRole.PARTNER,
    CharacterRole.AGENT,
    CharacterRole.CUSTOMER,
    CharacterRole.BENEFICIARY,
    CharacterRole.COLLABORATOR
  ]
} as const;

// ============================================================================
// SKILLS ENUMS
// ============================================================================

export enum CognitiveSkill {
  LOGIC = 'LOGIC',
  PERCEPTION = 'PERCEPTION',
  VISION = 'VISION',
  WISDOM = 'WISDOM',
  DECISION_MAKING = 'DECISION_MAKING',
  CREATIVITY = 'CREATIVITY',
  PROBLEM_SOLVING = 'PROBLEM_SOLVING',
  MEMORY = 'MEMORY',
  PLANNING = 'PLANNING',
  ORGANIZATION = 'ORGANIZATION',
  TIME_MANAGEMENT = 'TIME_MANAGEMENT',
  CONCENTRATION = 'CONCENTRATION',
}

export enum EmotionalSkill {
  SELF_AWARENESS = 'SELF_AWARENESS',
  EMOTION_CONTROL = 'EMOTION_CONTROL',
  SELF_CONTROL = 'SELF_CONTROL',
  DETERMINATION = 'DETERMINATION',
  INITIATIVE = 'INITIATIVE',
  RESILIENCE = 'RESILIENCE',
  EMPATHY = 'EMPATHY',
  INTEGRITY = 'INTEGRITY',
  CHARISMA = 'CHARISMA',
  LEADERSHIP = 'LEADERSHIP',
  COMMUNICATION = 'COMMUNICATION',
}

export enum PracticalSkill {
  FITNESS = 'FITNESS',
  DESIGN_THINKING = 'DESIGN_THINKING',
  PROJECT_MANAGEMENT = 'PROJECT_MANAGEMENT',
  TEACHING = 'TEACHING',
  NEGOTIATION = 'NEGOTIATION',
  NARRATIVE = 'NARRATIVE',
  DEVELOPING = 'DEVELOPING',
  HANDCRAFTING = 'HANDCRAFTING',
  PAINTING = 'PAINTING',
  ILLUSTRATION = 'ILLUSTRATION',
}
